var m = require('mithril');
var go = require('gojs');
var edit = require('../../../../layout/components/edit');
var diagramConfiguration = require('../diagramConfiguration');
var editCustomEquipmentModal = require('../../../editCustomEquipmentModal');
var { renderStaticSvg: renderStaticSvgRenderer } = require('./helpers/staticSvgRenderer');
var loaders = require('../../../../layout/components/loaders');
var { bindingChannel } = require('../../bindingChannel');
var createExternalConnectionAnnotations = require('./helpers/externalConnectionAnnotations');
var gridEquipmentNode = require('./helpers/gridEquipmentNode');
var resizableEquipment = require('./helpers/resizableEquipment');
var svgGridSizing = require('./helpers/svgGridSizing');

const $ = go.GraphObject.make;  // for more concise visual tree definitions
const portCoverBrush = $(go.Brush, "Linear", { 0: "#2a2a2a", 1: "#f0f0f0", start: go.Spot.Bottom, end: go.Spot.Top });
const equipmentGridDefaults = { cols: 7, rows: 5 };

function createEquipmentContextMenu(submitCallback) {
  return edit.vm.editing() ? $("ContextMenu",
    $("ContextMenuButton",
      $(go.TextBlock, "Edit Equipment"),
      {
        click: (e, obj) => {
          editCustomEquipmentModal.editDialogue({ ...obj.part.data }, { submitCallback: submitCallback, delete: true })
          m.redraw();
        }
      }
    ),
    $("ContextMenuButton",
      $(go.TextBlock, "Copy Bindings"),
      {
        click: (e, obj) => {
          window.latestBindingData = null;
          window.latestBindingData = {
            originRid: obj.part.data.key
          };
          localStorage.setItem("gojsBindingClipboard", JSON.stringify(window.latestBindingData));
          bindingChannel.postMessage({
            type: "copyBinding",
            data: window.latestBindingData
          });
          m.redraw();
        }
      }
    ),
    $("ContextMenuButton",
      new go.Binding("visible", "", () => {
        return !window.latestBindingData || Object.keys(window.latestBindingData).length === 0 ? false : true;
      }),
      $(go.TextBlock, "Paste Bindings"),
      {
        click: async (e, obj) => {
          let url = `/api/v1/diagram/${obj.part.data.key.slice(1)}/pastebindings`;
          await loaders.requestWithLoader({
            url,
            method: "PUT",
            body: {
              originRid: window.latestBindingData.originRid,
            }
          })
          .then((response) => {
            return submitCallback();
          })
          .catch((error) => {
            console.error("Error:", error);
          })
          .finally(() => {
            window.latestBindingData = null;
            localStorage.removeItem("gojsBindingClipboard");
          });
        }
      }
    ),
    $("ContextMenuButton",
      $(go.TextBlock, "Edit Permissions"),
      {
        click: (e, obj) => {
          m.route.set(`/equipment/${obj.part.data['class']}/${obj.part.data['key'].toString().slice(1)}/permissions`)
        }
      }
    )
  ) : null;
}

function createGridPortOptions(defaults, isCenterPort) {
  return {
    defaults: defaults || equipmentGridDefaults,
    isCenterPort: isCenterPort === true,
    isEditing: function() {
      return edit.vm.editing();
    },
    portCoverBrush: portCoverBrush
  };
}

function createGridStaticSvgNodeTemplate(nodeDrawing, staticSvgBindingPanel, useGeoPositioning, map, submitCallback, gridDefaults) {
  var defaults = gridDefaults || equipmentGridDefaults;
  return $(go.Node, "Spot",
    {
      locationSpot: go.Spot.TopLeft,
      locationObjectName: "BODY",
      selectionObjectName: "BODY",
      selectionAdornmentTemplate: gridEquipmentNode.createSelectionAdornmentTemplate(),
      resizeObjectName: "BODY",
      resizeCellSize: gridEquipmentNode.getResizeCellSize(),
      contextMenu: createEquipmentContextMenu(submitCallback),
      doubleClick: function (e, obj) {
        if (!edit.vm.editing()) {
          m.route.set(`/equipment/${obj.part.data['class']}/${obj.part.data['key'].toString().slice(1)}`)
        }
      }
    },
    new go.Binding("cursor", "", data => {
      return edit.vm.editing() ? "pointer" : "default";
    }),
    new go.Binding("deletable", "", data => {
      return edit.vm.editing();
    }),
    new go.Binding("selectable", "", data => {
      return edit.vm.editing();
    }),
    new go.Binding("movable", "", data => {
      return edit.vm.editing();
    }),
    new go.Binding("resizable", "", data => {
      return edit.vm.editing();
    }),
    useGeoPositioning ?
      new go.Binding("location", "latlong", data => {
        const pos = map.project(data, map.getZoom());
        return new go.Point(pos.x, pos.y);
      }).makeTwoWay((pt, data) => {
        if (updatingGoJs) {
          return data.latlong;
        } else {
          const ll = map.unproject(L.point(pt.x, pt.y), map.getZoom());
          return [ll.lat, ll.lng];
        }
      })
    :
      new go.Binding("location", "latlong", data => {
        return new go.Point(data[0], data[1]);
      }),
    $(go.Panel, "Spot",
      {
        name: "BODY",
        isPanelMain: true
      },
      gridEquipmentNode.createBodySizeBinding(defaults),
      $(go.Shape, "Rectangle",
        {
          isPanelMain: true,
          fill: "transparent",
          stroke: null,
          stretch: go.GraphObject.Fill
        }),
      $(go.Panel, "Viewbox",
        {
          stretch: go.GraphObject.Fill,
          pickable: false,
          isActionable: false
        },
        nodeDrawing
      ),
      staticSvgBindingPanel
    ),
    gridEquipmentNode.createSpotPortPanel("leftArray", "left", createGridPortOptions(defaults)),
    gridEquipmentNode.createSpotPortPanel("topArray", "top", createGridPortOptions(defaults)),
    gridEquipmentNode.createSpotPortPanel("rightArray", "right", createGridPortOptions(defaults)),
    gridEquipmentNode.createSpotPortPanel("bottomArray", "bottom", createGridPortOptions(defaults)),
    gridEquipmentNode.createSpotPortPanel("centerArray", "center", createGridPortOptions(defaults, true))
  );
}

function createStaticSvgTemplate(map, useGeoPositioning, submitCallback, diagram, nodeData) {
  function handlePointsLinkedData(pointsLinkedData) {
    if (!pointsLinkedData) {
      return [];
    }

    const fakeColumns = (pointsLinkedData.pointsLinked || []).map(pointLinked => {
      return {
        in: {
          '@rid': pointLinked.rid,
          value: '0',
          nodeKey: pointsLinkedData.nodeKey,
          equipmentClass: 'StaticSvg',
          formula: pointsLinkedData.formula,
          pointsLinked: pointsLinkedData.pointsLinked,
          staticSvgs: pointsLinkedData.staticSvgs || [],
          equipmentName: pointsLinkedData.equipmentName,
        }
      };
    });

    pointsLinkedData.fakeColumns = fakeColumns;

    fakeColumns.forEach(fakeColumn => {
      if (fakeColumn && fakeColumn.in && fakeColumn.in['@rid']) {
        const dataKey = `${pointsLinkedData.nodeKey}_${fakeColumn.in['@rid'].toString()}`;
        if (
          !diagram.model.modelData[dataKey] &&
          diagram.model.modelData[dataKey] !== '0'
        ) {
          diagram.model.commit(function(mod) {
            mod.set(mod.modelData, dataKey, '0');
          });
        }
        if (!edit.vm.editing()) {
          const pointSubscriberObj = diagram && (diagram.pointSubscriber || diagram.pointSubscriberObj);
          if (pointSubscriberObj) {
            pointSubscriberObj.subscribe({ column: fakeColumn }, diagram);
          }
        }
      }
    });

    const node = diagram.findNodeForKey(pointsLinkedData.nodeKey);
    if (node) {
      const values = (pointsLinkedData.pointsLinked || []).map(pointLinked => {
        return diagram.model.modelData[`${pointsLinkedData.nodeKey}_${pointLinked.rid}`];
      });
      renderStaticSvg(
        node,
        {
          staticSvgs: pointsLinkedData.staticSvgs || [],
          formula: pointsLinkedData.formula,
          pointValues: values,
          equipmentName: pointsLinkedData.equipmentName,
          rid: pointsLinkedData.nodeKey,
          svgConverted: nodeData ? nodeData.svgConverted : undefined
        }
      );
    }

    return fakeColumns;
  }

  var nodeDrawing = $(go.Panel, "Position",
    {
      row: 1,
      column: 1,
      name: "body"
    }
  );

  var staticSvgBindingPanel = $(go.Panel, "Spot",
    {
      name: "StaticSvgPanel",
      row: 1,
      column: 1,
      alignment: go.Spot.Center,
      visible: false,
      isActionable: false,
      pickable: false
    },
    new go.Binding("itemArray", "pointsLinkedData", handlePointsLinkedData)
  );

  if (nodeData && (nodeData.gridShell || nodeData.gridShellPrototype)) {
    var svgGridDefaults = svgGridSizing.getGridDefaultsForStaticSvgNode(nodeData, equipmentGridDefaults);
    return createGridStaticSvgNodeTemplate(nodeDrawing, staticSvgBindingPanel, useGeoPositioning, map, submitCallback, svgGridDefaults);
  }

  var nodePart = $(go.Node, "Spot",
    {
      locationSpot: go.Spot.TopLeft,
      resizeObjectName: "BODY",
      resizeCellSize: resizableEquipment.getResizeCellSize(),
      contextMenu: edit.vm.editing() ? $("ContextMenu",
        $("ContextMenuButton",
          $(go.TextBlock, "Edit Equipment"),
          {
            click: (e, obj) => {
              editCustomEquipmentModal.editDialogue({ ...obj.part.data }, { submitCallback: submitCallback, delete: true })
              m.redraw();
            }
          }
        ),
        $("ContextMenuButton",
          $(go.TextBlock, "Copy Bindings"),
          {
            click: (e, obj) => {
              window.latestBindingData = null;
              window.latestBindingData = {
                originRid: obj.part.data.key
              };
              localStorage.setItem("gojsBindingClipboard", JSON.stringify(window.latestBindingData));
              bindingChannel.postMessage({
                type: "copyBinding",
                data: window.latestBindingData
              });
              m.redraw();
            }
          }
        ),
        $("ContextMenuButton",
          new go.Binding("visible", "", () => {
            return !window.latestBindingData || Object.keys(window.latestBindingData).length === 0 ? false : true;
          }),
          $(go.TextBlock, "Paste Bindings"),
          {
            click: async (e, obj) => {
              let url = `/api/v1/diagram/${obj.part.data.key.slice(1)}/pastebindings`;
              await loaders.requestWithLoader({
                url,
                method: "PUT",
                body: {
                  originRid: window.latestBindingData.originRid,
                }
              })
              .then((response) => {
                return submitCallback();
              })
              .catch((error) => {
                console.error("Error:", error);
              })
              .finally(() => {
                window.latestBindingData = null;
                localStorage.removeItem("gojsBindingClipboard");
              });
            }
          }
        ),
        $("ContextMenuButton",
          $(go.TextBlock, "Edit Permissions"),
          {
            click: (e, obj) => {
              m.route.set(`/equipment/${obj.part.data['class']}/${obj.part.data['key'].toString().slice(1)}/permissions`)
            }
          }
        )
      ) : null,
      doubleClick: function (e, obj) {
        if (!edit.vm.editing()) {
          m.route.set(`/equipment/${obj.part.data['class']}/${obj.part.data['key'].toString().slice(1)}`)
        }
      }
    },
    new go.Binding("cursor", "", data => {
      return edit.vm.editing() ? "pointer" : "default";
    }),
    new go.Binding("deletable", "", data => {
      return edit.vm.editing();
    }),
    new go.Binding("selectable", "", data => {
      return edit.vm.editing();
    }),
    new go.Binding("movable", "", data => {
      return edit.vm.editing();
    }),
    resizableEquipment.createResizableBinding(),
    useGeoPositioning ?
      new go.Binding("location", "latlong", data => {
        const pos = map.project(data, map.getZoom());
        return new go.Point(pos.x, pos.y);
      }).makeTwoWay((pt, data) => {
        if (updatingGoJs) {
          return data.latlong;
        } else {
          const ll = map.unproject(L.point(pt.x, pt.y), map.getZoom());
          return [ll.lat, ll.lng];
        }
      })
    :
      new go.Binding("location", "latlong", data => {
        return new go.Point(data[0], data[1]);
      }),
    $(go.Panel, "Table",
      $(go.Panel, "Auto",
        {
          row: 1, column: 1, name: "BODY",
          stretch: go.GraphObject.Fill
        },
        ...resizableEquipment.createBodySizeBindings(),
        nodeDrawing,
        staticSvgBindingPanel
      ),
      $(go.Panel, "Vertical",
        new go.Binding("itemArray", "leftArray"),
        {
          row: 1, column: 0,
          itemTemplate:
            $(go.Panel,
              {
                _side: "left",
                fromSpot: go.Spot.Left,
                toSpot: go.Spot.Left,
                cursor: "pointer",
              },
              new go.Binding("portId", "portId", function(portId) {
                return portId.split('_')[1];
              }),
              new go.Binding("category", "category"),
              new go.Binding("fromLinkable", "type", function(type) {
                return type === "output";
              }),
              new go.Binding("toLinkable", "type", function(type) {
                return type === "input";
              }),
              $(go.Shape, "Rectangle",
                {
                  stroke: null, strokeWidth: 0,
                  desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight()),
                  margin: new go.Margin(diagramConfiguration.calculateCellHeight() / 2, 0)
                },
                new go.Binding("fill", "portColor", function(portColor) {
                  if(!edit.vm.editing()) {
                    return portCoverBrush;
                  } else {
                    return portColor;
                  }
                }))
            )
        }
      ),
      $(go.Panel, "Horizontal",
        new go.Binding("itemArray", "topArray"),
        {
          row: 0, column: 1,
          itemTemplate:
            $(go.Panel,
              {
                _side: "top",
                fromSpot: go.Spot.Top,
                toSpot: go.Spot.Top,
                cursor: "pointer",
              },
              new go.Binding("portId", "portId", function(portId) {
                return portId.split('_')[1];
              }),
              new go.Binding("category", "category"),
              new go.Binding("fromLinkable", "type", function(type) {
                return type === "output";
              }),
              new go.Binding("toLinkable", "type", function(type) {
                return type === "input";
              }),
              $(go.Shape, "Rectangle",
                {
                  stroke: null, strokeWidth: 0,
                  desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight()),
                  margin: new go.Margin(0, diagramConfiguration.calculateCellWidth() / 2)
                },
                new go.Binding("fill", "portColor", function(portColor) {
                  if(!edit.vm.editing()) {
                    return portCoverBrush;
                  } else {
                    return portColor;
                  }
                }))
            )
        }
      ),
      $(go.Panel, "Vertical",
        new go.Binding("itemArray", "rightArray"),
        {
          row: 1, column: 2,
          itemTemplate:
            $(go.Panel,
              {
                _side: "right",
                fromSpot: go.Spot.Right,
                toSpot: go.Spot.Right,
                cursor: "pointer",
              },
              new go.Binding("portId", "portId", function(portId) {
                return portId.split('_')[1];
              }),
              new go.Binding("category", "category"),
              new go.Binding("fromLinkable", "type", function(type) {
                return type === "output";
              }),
              new go.Binding("toLinkable", "type", function(type) {
                return type === "input";
              }),
              $(go.Shape, "Rectangle",
                {
                  stroke: null, strokeWidth: 0,
                  desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight()),
                  margin: new go.Margin(diagramConfiguration.calculateCellHeight() / 2, 0)
                },
                new go.Binding("fill", "portColor", function(portColor) {
                  if(!edit.vm.editing()) {
                    return portCoverBrush;
                  } else {
                    return portColor;
                  }
                }))
            )
        }
      ),
      $(go.Panel, "Horizontal",
        new go.Binding("itemArray", "bottomArray"),
        {
          row: 2, column: 1,
          itemTemplate:
            $(go.Panel,
              {
                _side: "bottom",
                fromSpot: go.Spot.Bottom,
                toSpot: go.Spot.Bottom,
                cursor: "pointer",
              },
              new go.Binding("portId", "portId", function(portId) {
                return portId.split('_')[1];
              }),
              new go.Binding("category", "category"),
              new go.Binding("fromLinkable", "type", function(type) {
                return type === "output";
              }),
              new go.Binding("toLinkable", "type", function(type) {
                return type === "input";
              }),
              $(go.Shape, "Rectangle",
                {
                  stroke: null, strokeWidth: 0,
                  desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight()),
                  margin: new go.Margin(0, diagramConfiguration.calculateCellWidth() / 2)
                },
                new go.Binding("fill", "portColor", function(portColor) {
                  if(!edit.vm.editing()) {
                    return portCoverBrush;
                  } else {
                    return portColor;
                  }
                }))
            )
        }
      ),
      $(go.Panel, "Horizontal",
        new go.Binding("itemArray", "centerArray"),
        {
          row: 1, column: 1,
          itemTemplate:
            $(go.Panel,
              {
                alignment: go.Spot.Center,
                fromSpot: go.Spot.AllSides,
                toSpot: go.Spot.AllSides,
                fromLinkable: true,
                toLinkable: true,
                fromMaxLinks: 1,
                toMaxLinks: 1,
                visible: edit.vm.editing() ? true : false,
              },
              new go.Binding("portId", "portId", function(portId) {
                return portId.split('_')[1];
              }),
              new go.Binding("category", "category"),
              $(go.Shape, "Rectangle",
                {
                  stroke: null, strokeWidth: 0,
                  desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight()),
                  margin: new go.Margin(0, diagramConfiguration.calculateCellWidth() / 2),
                },
                new go.Binding("fill", "portColor", function(portColor) {
                  if(!edit.vm.editing()) {
                    return portCoverBrush;
                  } else {
                    return portColor;
                  }
                }))
            )
        }
      ),
      createExternalConnectionAnnotations()
    )
  );

  return nodePart;
}

function renderStaticSvg(node, options) {
  return renderStaticSvgRenderer(node, options);
}

module.exports = {
  createStaticSvgTemplate,
  renderStaticSvg
};
